/**
 * Toast content sanitizer.
 *
 * Wren toasts must stay emoji-free (ADHD-friendly, on-brand).
 * This module strips emoji from toast titles/descriptions at the source so
 * future toast() callers can't accidentally reintroduce them — even if a
 * localization string or fallback gains an emoji later.
 *
 * Allowed exception: the typographic checkmark "✓" (U+2713) is preserved
 * because it's used as a textual confirmation marker (e.g. "Logged ✓").
 */

import * as React from "react";

// Match common emoji ranges (incl. variation selectors and ZWJs).
// Mirrors the regex in AssistantMessageContent for consistency.
const EMOJI_RE =
  /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F000}-\u{1F2FF}\u{1F900}-\u{1F9FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu;

// Preserve the typographic checkmark used as a text marker.
const ALLOWED = new Set(["\u2713"]);

/**
 * Strip emoji from a string while preserving allowed glyphs (e.g. "✓").
 * Collapses any whitespace that emoji removal leaves behind.
 */
export function stripEmoji(input: string): string {
  if (!input) return input;
  const cleaned = input.replace(EMOJI_RE, (match) => (ALLOWED.has(match) ? match : ""));
  return cleaned.replace(/\s{2,}/g, " ").trim();
}

/**
 * Sanitize any toast field (title or description). Strings are stripped of
 * emoji; React nodes pass through unchanged (caller is responsible for any
 * JSX content — typically icons via Lucide, not emoji).
 */
export function sanitizeToastField(value: React.ReactNode): React.ReactNode {
  if (typeof value === "string") return stripEmoji(value);
  return value;
}
